// User Profile Service Interface - Manages user profiles and onboarding

/**
 * Interface for user profile service implementations
 * Handles profile creation, retrieval, and onboarding flow
 */
export class IUserProfileService {
  /**
   * Check if a wallet address has an existing profile
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{exists: boolean, profile?: any, error?: string}>}
   */
  async checkProfileExists(walletAddress) {
    throw new Error('Method must be implemented by concrete class');
  }

  /**
   * Create a new user profile on-chain
   * @param {string} walletAddress - User's wallet address
   * @param {string} username - Chosen username
   * @param {string} bio - User bio
   * @param {string} avatarUrl - Avatar image URL
   * @returns {Promise<{success: boolean, txId?: string, profile?: any, error?: string}>}
   */
  async createProfile(walletAddress, username, bio = '', avatarUrl = '') {
    throw new Error('Method must be implemented by concrete class');
  }

  /**
   * Get user profile by wallet address
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{success: boolean, profile?: any, error?: string}>}
   */
  async getProfile(walletAddress) {
    throw new Error('Method must be implemented by concrete class');
  }

  /**
   * Update existing user profile
   * @param {string} walletAddress - User's wallet address
   * @param {Object} updates - Profile updates
   * @returns {Promise<{success: boolean, txId?: string, profile?: any, error?: string}>}
   */
  async updateProfile(walletAddress, updates) {
    throw new Error('Method must be implemented by concrete class');
  }

  /**
   * Save profile creation transaction ID for tracking
   * @param {string} walletAddress - User's wallet address
   * @param {string} txId - Transaction ID
   * @param {Object} profileData - Profile data
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async saveProfileTransaction(walletAddress, txId, profileData) {
    throw new Error('Method must be implemented by concrete class');
  }

  /**
   * Get profile creation transaction status
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{success: boolean, txId?: string, status?: string, error?: string}>}
   */
  async getProfileTransactionStatus(walletAddress) {
    throw new Error('Method must be implemented by concrete class');
  }

  /**
   * Check if username is available
   * @param {string} username - Username to check
   * @returns {Promise<{available: boolean, error?: string}>}
   */
  async checkUsernameAvailability(username) {
    throw new Error('Method must be implemented by concrete class');
  }

  /**
   * Get user onboarding status
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{status: 'new'|'pending'|'completed', profile?: any, txId?: string}>}
   */
  async getOnboardingStatus(walletAddress) {
    throw new Error('Method must be implemented by concrete class');
  }
}
